/* header-color-change.js — partner colours on the LEGACY templates.
 *
 * Reads the three maps in assets/headerColors.js and paints them, with inline
 * styles, onto the elements that opt in.
 *
 * WHICH elements changed on 2026-09-19. Until then this file painted, by
 * class, the whole legacy chrome:
 *   .dynamic__header        header background + text colour
 *   .dynamic_primary_color  button background + text colour, with hover colours
 *   .dynamic_footer         footer background + text colour
 * Luis's call, after looking at ?code=DRB on the preview: partner identity
 * carries on the LOGO, and the chrome — header band, nav row, footer bar —
 * and every primary CTA keep the STS navy on a co-branded page, exactly as an
 * unbranded visit gets them. assets/sts-chrome.js applies that to the v4
 * chrome; this file has to apply it to the legacy templates too, or the same
 * visitor gets a taupe header on every page the v4 header does not own and the
 * two halves of the site disagree about what a partner colour means.
 *
 * So the three class selectors are gone and the target list is the same opt-in
 * attribute assets/sts-chrome.js reads:
 *   [data-sts-affiliate-paint="header"|"button"|"footer"]
 * Nothing in the theme carries it today, so a coded visit paints nothing.
 * Everything else here — the code resolution, the measured ink, the hover
 * pair, the marks and the window.STS.affiliateContrast record — is unchanged
 * and ready for whatever surface is opted in next.
 *
 * The v4 chrome is still skipped outright (spec 03 §7.4, W-A2b):
 *   [data-sts-header], [data-sts-footer], [data-sts-btn-primary]
 * assets/sts-chrome.js owns anything inside those, from the same maps and the
 * same measurement, through custom properties that assets/sts-chrome.css
 * reads. An inline background-color from this file would beat those var()
 * rules, and an inline `color` on a whole bar would re-ink children the v4 CSS
 * inks on purpose, so the two scripts would fight over one element.
 *
 * Wrapped in an IIFE: the old top-level `var code` and `function
 * getURLParameter` leaked onto window, and assets/quiz.js declares its own
 * global getURLParameter with different side effects.
 */
(function () {
  'use strict';

  var V4_CHROME = '[data-sts-header], [data-sts-footer], [data-sts-btn-primary]';

  /* The opt-in hook, the same attribute assets/sts-chrome.js groups by. */
  var PAINT_ATTR = 'data-sts-affiliate-paint';
  function optIn(name) { return '[' + PAINT_ATTR + '="' + name + '"]'; }

  function getURLParameter(name) {
    return decodeURIComponent((new RegExp('[?|&]' + name + '=' + '([^&;]+?)(&|#|;|$)').exec(location.search) || [, ''])[1].replace(/\+/g, '%20')) || null;
  }

  function storedCode() {
    try {
      return localStorage.getItem('affiliateCode') || sessionStorage.getItem('affiliateCode');
    } catch (e) {
      return null; // storage blocked
    }
  }

  // The 'code' parameter from the URL, else from local/session storage. A
  // provider link carries the provider id after a dash (?code=DRB-12); the
  // affiliate code is the part before it, as in assets/change-logo.js.
  var code = getURLParameter('code') || storedCode();
  if (!code) return;
  code = String(code).split('-')[0].trim().toUpperCase();
  if (!code) return;

  function isV4(el) {
    return !!(el.closest && el.closest(V4_CHROME));
  }

  /* The ink on a partner colour is measured, not assumed — the same rule the v4
     chrome applies, from the same implementation. Every map in
     assets/headerColors.js declares `color: 'white'`, and on the real
     backgrounds most of those pairs are under WCAG AA 4.5:1; the partner
     footers are at 1.48:1 and 1.92:1. The maths lives in assets/sts-chrome.js
     (window.STS.contrast) so there is one copy of it. When that file has not
     run — a layout without the v4 chrome — the partner's declared colour is
     used exactly as before, so nothing here regresses. */
  function ink(bg, declared, prefer) {
    try {
      var C = window.STS && window.STS.contrast;
      if (C && typeof C.pick === 'function') return C.pick(bg, declared, prefer);
    } catch (e) { /* never break a page */ }
    return { bg: bg, declared: declared, fg: declared, white: null, black: null, ratio: null, aa: null, measured: false };
  }

  /* Same observability the v4 chrome gives QA: the chosen ink and the ratio
     that chose it, on the element and on window.STS.affiliateContrast. */
  function record(name, m, el, suffix) {
    try {
      if (el && m.measured) {
        el.setAttribute('data-sts-ink' + suffix, m.fg === '#ffffff' ? 'light' : 'dark');
        el.setAttribute('data-sts-contrast' + suffix, m.ratio.toFixed(2));
        if (m.aa === false) el.setAttribute('data-sts-contrast-fail' + suffix, '');
      }
      /* `unreadable`, not `failing`: in the v4 report `failing` is the list of
         TEXT NODES measured under AA, and a surface name dropped in among them
         would make that list lie about what it counted. */
      var S = (window.STS = window.STS || {});
      var r = (S.affiliateContrast = S.affiliateContrast ||
        { code: code, threshold: 4.5, surfaces: {}, unreadable: [], failing: [] });
      r.surfaces[name] = m;
      if (!r.unreadable) r.unreadable = [];
      if (m.aa === false && r.unreadable.indexOf(name) < 0) r.unreadable.push(name);
    } catch (e) { /* never break a page */ }
  }

  /* Named for what it is: the opt-in elements OUTSIDE the v4 chrome. */
  function eachLegacy(selector, fn) {
    var list = document.querySelectorAll(selector);
    for (var i = 0; i < list.length; i++) {
      if (!isV4(list[i])) fn(list[i]);
    }
  }

  function run() {
    var headerStyles = window.headerColors && window.headerColors[code];
    var buttonStyles = window.buttonColors && window.buttonColors[code];
    var footerStyles = window.footerColors && window.footerColors[code];

    // Header
    if (headerStyles) {
      var headerInk = ink(headerStyles.background, headerStyles.color);
      eachLegacy(optIn('header'), function (header) {
        header.style.backgroundColor = headerStyles.background;
        header.style.color = headerInk.fg;
        record('legacyHeader', headerInk, header, '');
      });
    }

    // Primary button, text and hover
    if (buttonStyles) {
      /* The hover background can be absent; the base colour then stands in, as
         it does in the v4 path, so the button still reads as the same button. */
      var hoverBg = buttonStyles.hoverBackground || buttonStyles.background;
      var baseInk = ink(buttonStyles.background, buttonStyles.color);
      var hoverInk = ink(hoverBg, buttonStyles.hoverColor || buttonStyles.color,
        baseInk.measured ? baseInk.fg : null);
      eachLegacy(optIn('button'), function (button) {
        button.style.backgroundColor = buttonStyles.background;
        button.style.color = baseInk.fg;
        record('legacyButton', baseInk, button, '');
        record('legacyButtonHover', hoverInk, button, '-hover');

        button.addEventListener('mouseover', function () {
          button.style.backgroundColor = hoverBg;
          button.style.color = hoverInk.fg;
        });
        button.addEventListener('mouseout', function () {
          button.style.backgroundColor = buttonStyles.background;
          button.style.color = baseInk.fg;
        });
      });
    }

    // Footer
    if (footerStyles) {
      var footerInk = ink(footerStyles.background, footerStyles.color);
      eachLegacy(optIn('footer'), function (footer) {
        footer.style.backgroundColor = footerStyles.background;
        footer.style.color = footerInk.fg;
        record('legacyFooter', footerInk, footer, '');
      });
    }
  }

  // Loaded with defer, so the document is parsed by now. The readyState check
  // only matters if this file is ever loaded without defer.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
